import { createContext, use, useEffect, useMemo, ReactNode } from 'react';
import { User, Plan } from '../types';
import type { VisaPreparation, ConsultingOffer } from '@preparation';
import { useMe } from '../session/hooks/useMe';
import { identifyUser } from '@/shared/analytics';
import { reportAdClicks, type PaymentGateway } from '../api';

interface AuthContextValue {
  user: User | null;
  plan: Plan | null;
  gateway: PaymentGateway | null;
  visaPreparation: VisaPreparation | null;
  consulting: ConsultingOffer | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { data, isLoading } = useMe();
  const user = data?.user ?? null;

  useEffect(() => {
    if (user) {
      identifyUser(user.id, user.email, user.name);
      void reportAdClicks();
    }
  }, [user]);

  const value = useMemo<AuthContextValue>(() => ({
    user,
    plan: data?.plan ?? null,
    gateway: data?.gateway ?? null,
    visaPreparation: data?.visa_preparation ?? null,
    consulting: data?.consulting ?? null,
    isLoading,
    isAuthenticated: !!user,
  }), [data, user, isLoading]);

  return (
    <AuthContext value={value}>
      {children}
    </AuthContext>
  );
}

export function useAuth() {
  const context = use(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
